'use client';

import { useState } from 'react';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';
import { useWebTheme, type ThemePreference } from '@/components/WebThemeProvider';
import type { Locale } from '@/i18n/public';

const labels = {
  cs: { appearance: 'Vzhled', system: 'Podle systému', light: 'Světlý', dark: 'Tmavý' },
  en: { appearance: 'Appearance', system: 'Use system setting', light: 'Light', dark: 'Dark' },
  de: { appearance: 'Darstellung', system: 'Systemeinstellung', light: 'Hell', dark: 'Dunkel' },
} as const;

const options: ThemePreference[] = ['system', 'light', 'dark'];

function PreferenceIcon({ preference }: { preference: ThemePreference }) {
  if (preference === 'light') return <LightModeRoundedIcon fontSize="small" />;
  if (preference === 'dark') return <DarkModeRoundedIcon fontSize="small" />;
  return <ComputerRoundedIcon fontSize="small" />;
}

export function ThemeModeSwitcher({ locale }: { locale: Locale }) {
  const { preference, setPreference } = useWebTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const copy = labels[locale];
  const currentLabel = copy[preference];

  return (
    <>
      <Tooltip title={`${copy.appearance}: ${currentLabel}`}>
        <IconButton
          color="inherit"
          aria-label={`${copy.appearance}: ${currentLabel}`}
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <PreferenceIcon preference={preference} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map((option) => (
          <MenuItem
            key={option}
            selected={preference === option}
            onClick={() => {
              setPreference(option);
              setAnchor(null);
            }}
          >
            <ListItemIcon><PreferenceIcon preference={option} /></ListItemIcon>
            {copy[option]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
